<template>
  <div>
    <label
      :class="[
        optionClass,
        active ? optionClassActive : 'hover:bg-gray-200 dark:hover:bg-gray-800 transition-all duration-200'
      ]"
    >
      <input
        :id="id"
        ref="input"
        v-model="currentValue"
        :value="value"
        :autofocus="autofocus"
        :readonly="readonly"
        :disabled="disabled"
        :checked="active"
        :name="name"
        :required="required"
        :class="currentClass"
        type="radio"
        @blur="onBlur"
        @focus="onFocus"
      >
      <!--<span v-if="label" class="ml-3 text-gray-800">
        {{ label }}
      </span>-->
      <slot :checked="active">
        <label
          v-if="label"
          :class="labelClass"
          :for="id"
        >
          <div>
            {{ label }}
            <span v-if="info" class="block opacity-70 text-sm">
              {{ info }}
            </span>
          </div>
          <div v-html="control" />
        </label>
      </slot>
    </label>
  </div>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'

interface Props {
  value?: string | Record<string, any> | number | boolean
  label?: string | null
  info?: string | null
  control?: string | null
  checked?: boolean | string
  modelValue?: string | Record<string, any> | number | boolean | null
  readonly?: boolean
  required?: boolean
  baseClass?: string | Record<string, any> | string[]
  optionClass?: string | Record<string, any> | string[]
  optionClassActive?: string | Record<string, any> | string[]
  labelClass?: string | Record<string, any> | string[]
  // attributes mixin
  id?: string | number | null
  autofocus?: boolean | null
  disabled?: boolean
  name?: string | null
  tabindex?: string | number | null
  // classes mixin
  size?: string | null
  status?: boolean | string | null
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
}

const props = withDefaults(defineProps<Props>(), {
  value: 'on',
  label: null,
  info: null,
  control: null,
  checked: false,
  modelValue: null,
  readonly: false,
  required: false,
  baseClass: 'o-radio appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded-full checked:bg-primary-600 checked:border-primary-600 flex-shrink-0',
  optionClass: 'p-1 flex items-center rounded transition duration-200',
  optionClassActive: 'bg-primary-100/50 dark:bg-primary-800/20',
  labelClass: 'w-full ml-2 opacity-80 flex justify-between items-center text-sm',
  id: null,
  autofocus: null,
  disabled: false,
  name: null,
  tabindex: null,
  size: null,
  status: null,
  defaultStatusClass: undefined,
  warningStatusClass: undefined,
  errorStatusClass: undefined,
  successStatusClass: undefined,
  disabledClass: undefined,
  defaultSizeClass: undefined,
  largeSizeClass: undefined,
  smallSizeClass: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
}>()

const input = ref<HTMLInputElement>()
const currentValue = ref<any>(props.checked ? props.value : props.modelValue)

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const active = computed(() => {
  return isEqual(currentValue.value, props.value)
})

watch(() => props.modelValue, (model) => {
  if (!isEqual(model, currentValue.value)) {
    currentValue.value = model
  }
})

watch(() => props.checked, (checked) => {
  const cv = checked ? props.value : null
  if (!isEqual(cv, currentValue.value)) {
    currentValue.value = cv
  }
})

watch(currentValue, (val) => {
  emit('update:modelValue', val)
  emit('change', val)
})

function onBlur (e: Event) {
  emit('blur', e)
}

function onFocus (e: Event) {
  emit('focus', e)
}
</script>

<style>
  .o-radio:checked[type=radio] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%22-4 -4 8 8%22%3E%3Ccircle r=%222%22 fill=%22%23fff%22/%3E%3C/svg%3E")
  }
</style>
